import { useNavigate } from 'react-router-dom';
import { FlaskConical, Beaker, Microscope, BookOpen, GraduationCap, Shield, ArrowRight, ChevronDown } from 'lucide-react';
import Watermark from '@/components/ui/Watermark';
import CopyrightFooter from '@/components/ui/CopyrightFooter';

const features = [
  {
    icon: <FlaskConical size={28} />,
    title: 'Interactive Experiments',
    description: 'Step through real laboratory procedures with guided simulations and real-time visual feedback.',
  },
  {
    icon: <Beaker size={28} />,
    title: 'Virtual Equipment',
    description: 'Work with laboratory instruments, reagents, and apparatus in a safe digital environment.',
  },
  {
    icon: <Microscope size={28} />,
    title: 'Data Collection',
    description: 'Record observations, measurements, and results just like a real laboratory notebook.',
  },
  {
    icon: <BookOpen size={28} />,
    title: 'Lab Manuals',
    description: 'Complete experiment summaries with procedures, safety info, and theoretical background.',
  },
  {
    icon: <GraduationCap size={28} />,
    title: 'Progress Tracking',
    description: 'Track your completion across all experiments and course modules with persistent progress.',
  },
  {
    icon: <Shield size={28} />,
    title: 'Safe Learning',
    description: 'Practice techniques without hazards — perfect for pre-lab preparation and remote learning.',
  },
];

const courses = [
  {
    code: 'CHEM 2131',
    name: 'Quantitative Analytical Chemistry Lab',
    experiments: 11,
    description: 'Water purification, spectrophotometry, titrations, gravimetric analysis, and quantitative techniques.',
  },
  {
    code: 'CHEM 1131',
    name: 'General Chemistry Lab I',
    experiments: 10,
    description: 'Fundamental techniques, measurements, solution preparation, and introductory chemical analysis.',
  },
];

const howItWorks = [
  {
    step: '1',
    title: 'Sign In',
    description: 'Sign in with your email, Google, or Microsoft account.',
  },
  {
    step: '2',
    title: 'Select Experiment',
    description: 'Choose from the available experiments in your course dashboard.',
  },
  {
    step: '3',
    title: 'Complete the Lab',
    description: 'Follow guided steps, interact with equipment, and record your results.',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="landing">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="nav-inner">
          <div className="nav-brand">
            <div className="nav-logo">
              <FlaskConical size={22} />
            </div>
            <span className="nav-title">BDP vLab</span>
          </div>
          <div className="nav-links">
            <a href="#features" className="nav-link">Features</a>
            <a href="#courses" className="nav-link">Courses</a>
            <a href="#how-it-works" className="nav-link">How It Works</a>
            <button className="nav-btn outline" onClick={() => navigate('/login')}>Sign In</button>
            <button className="nav-btn primary" onClick={() => navigate('/signup')}>
              Get Started <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-content">
            <div className="hero-badge">Virtual Laboratory Platform</div>
            <h1>Master Science Through Interactive Lab Simulations</h1>
            <p>
              Practice real laboratory techniques in a safe, guided digital environment.
              Complete experiments, collect data, and build confidence before stepping into the lab.
            </p>
            <div className="hero-actions">
              <button className="hero-btn primary" onClick={() => navigate('/login')}>
                Enter the Lab <ArrowRight size={18} />
              </button>
              <button className="hero-btn outline" onClick={() => navigate('/signup')}>
                Create Account
              </button>
            </div>
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="stat-num">21</span>
                <span className="stat-label">Experiments</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="stat-num">2</span>
                <span className="stat-label">Courses</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="stat-num">100%</span>
                <span className="stat-label">Interactive</span>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-flask-container">
              <div className="hero-flask">
                <FlaskConical size={80} />
              </div>
              <div className="hero-bubble b1" />
              <div className="hero-bubble b2" />
              <div className="hero-bubble b3" />
            </div>
          </div>
        </div>
        <a href="#features" className="scroll-indicator">
          <ChevronDown size={24} />
        </a>
      </section>

      {/* Features Section */}
      <section id="features" className="section features-section">
        <div className="section-inner">
          <div className="section-header">
            <span className="section-tag">Features</span>
            <h2>Everything You Need for Lab Success</h2>
            <p>Our virtual lab platform provides comprehensive tools for science education.</p>
          </div>
          <div className="features-grid">
            {features.map((f, i) => (
              <div key={i} className="feature-card">
                <div className="feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section id="courses" className="section courses-section">
        <div className="section-inner">
          <div className="section-header">
            <span className="section-tag">Courses</span>
            <h2>Available Laboratory Courses</h2>
            <p>Select your course to begin your virtual lab experience.</p>
          </div>
          <div className="courses-grid">
            {courses.map((c, i) => (
              <div key={i} className="course-card">
                <div className="course-code">{c.code}</div>
                <h3>{c.name}</h3>
                <p>{c.description}</p>
                <div className="course-meta">
                  <span>{c.experiments} Experiments</span>
                  <button className="course-btn" onClick={() => navigate('/login')}>
                    Start Lab <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="section how-section">
        <div className="section-inner">
          <div className="section-header">
            <span className="section-tag">How It Works</span>
            <h2>Get Started in Three Simple Steps</h2>
            <p>From sign-in to completed experiment in minutes.</p>
          </div>
          <div className="steps-grid">
            {howItWorks.map((s, i) => (
              <div key={i} className="step-card">
                <div className="step-num">{s.step}</div>
                <h3>{s.title}</h3>
                <p>{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-inner">
          <h2>Ready to Begin Your Virtual Lab?</h2>
          <p>Join students and instructors already using BDP vLab for interactive science education.</p>
          <div className="cta-actions">
            <button className="cta-btn primary" onClick={() => navigate('/login')}>
              Sign In
            </button>
            <button className="cta-btn outline" onClick={() => navigate('/signup')}>
              Create Account
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <div className="nav-logo small">
              <FlaskConical size={16} />
            </div>
            <span>BDP vLab</span>
          </div>
          <div className="footer-links">
            <a href="#features">Features</a>
            <a href="#courses">Courses</a>
            <a href="#how-it-works">How It Works</a>
          </div>
          <div className="footer-copy">
            &copy; {new Date().getFullYear()} BDP vLab &mdash; Developed by Iraj Mehrnia
          </div>
        </div>
      </footer>

      <style>{`
        .landing {
          min-height: 100vh;
          background: #0F0F13;
          color: #F0F0F0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* Navigation */
        .landing-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          background: rgba(15, 15, 19, 0.85);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .nav-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 32px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .nav-brand {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .nav-logo {
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, #FF5A36, #00A86B);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }
        .nav-logo.small {
          width: 28px;
          height: 28px;
          border-radius: 8px;
        }
        .nav-title {
          font-size: 18px;
          font-weight: 700;
          color: #FFFFFF;
        }
        .nav-links {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .nav-link {
          color: rgba(255,255,255,0.6);
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          padding: 8px 14px;
          border-radius: 8px;
          transition: color 0.2s, background 0.2s;
        }
        .nav-link:hover {
          color: #FFFFFF;
          background: rgba(255,255,255,0.06);
        }
        .nav-btn {
          padding: 8px 18px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 6px;
          border: none;
        }
        .nav-btn.outline {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.2);
          color: #FFFFFF;
        }
        .nav-btn.outline:hover {
          border-color: rgba(255,255,255,0.4);
          background: rgba(255,255,255,0.06);
        }
        .nav-btn.primary {
          background: linear-gradient(135deg, #FF5A36, #E04420);
          color: white;
        }
        .nav-btn.primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(255, 90, 54, 0.3);
        }

        /* Hero */
        .hero {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          padding: 100px 32px 60px;
          background: radial-gradient(ellipse at 30% 50%, rgba(255,90,54,0.08) 0%, transparent 60%),
                      radial-gradient(ellipse at 70% 50%, rgba(0,168,107,0.06) 0%, transparent 60%),
                      #0F0F13;
        }
        .hero-inner {
          max-width: 1200px;
          width: 100%;
          display: flex;
          align-items: center;
          gap: 80px;
        }
        .hero-content {
          flex: 1;
        }
        .hero-badge {
          display: inline-block;
          padding: 6px 14px;
          background: rgba(255,90,54,0.12);
          border: 1px solid rgba(255,90,54,0.2);
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          color: #FF7B5C;
          margin-bottom: 24px;
        }
        .hero h1 {
          font-size: 48px;
          font-weight: 800;
          line-height: 1.15;
          margin: 0 0 20px;
          background: linear-gradient(135deg, #FFFFFF 30%, #A0A0A0 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .hero p {
          font-size: 18px;
          line-height: 1.7;
          color: rgba(255,255,255,0.6);
          margin: 0 0 32px;
          max-width: 520px;
        }
        .hero-actions {
          display: flex;
          gap: 14px;
          margin-bottom: 48px;
        }
        .hero-btn {
          padding: 14px 28px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
          border: none;
        }
        .hero-btn.primary {
          background: linear-gradient(135deg, #FF5A36, #E04420);
          color: white;
        }
        .hero-btn.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(255, 90, 54, 0.3);
        }
        .hero-btn.outline {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.2);
          color: #FFFFFF;
        }
        .hero-btn.outline:hover {
          border-color: rgba(255,255,255,0.4);
          background: rgba(255,255,255,0.06);
        }
        .hero-stats {
          display: flex;
          align-items: center;
          gap: 24px;
        }
        .hero-stat {
          display: flex;
          flex-direction: column;
        }
        .stat-num {
          font-size: 28px;
          font-weight: 800;
          color: #FFFFFF;
        }
        .stat-label {
          font-size: 13px;
          color: rgba(255,255,255,0.4);
          font-weight: 500;
        }
        .hero-stat-divider {
          width: 1px;
          height: 36px;
          background: rgba(255,255,255,0.1);
        }
        .hero-visual {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .hero-flask-container {
          position: relative;
          width: 260px;
          height: 260px;
        }
        .hero-flask {
          width: 260px;
          height: 260px;
          background: linear-gradient(135deg, rgba(255,90,54,0.15), rgba(0,168,107,0.15));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255,255,255,0.3);
          border: 1px solid rgba(255,255,255,0.06);
        }
        .hero-bubble {
          position: absolute;
          border-radius: 50%;
          background: rgba(255,90,54,0.2);
          animation: bubble 3s ease-in-out infinite;
        }
        .hero-bubble.b1 { width: 12px; height: 12px; bottom: 30px; left: 80px; animation-delay: 0s; }
        .hero-bubble.b2 { width: 8px; height: 8px; bottom: 50px; right: 60px; animation-delay: 1s; background: rgba(0,168,107,0.25); }
        .hero-bubble.b3 { width: 10px; height: 10px; bottom: 20px; left: 140px; animation-delay: 2s; }
        @keyframes bubble {
          0%, 100% { transform: translateY(0); opacity: 0.6; }
          50% { transform: translateY(-20px); opacity: 1; }
        }
        .scroll-indicator {
          position: absolute;
          bottom: 24px;
          color: rgba(255,255,255,0.3);
          animation: bounce 2s ease-in-out infinite;
          text-decoration: none;
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(8px); }
        }

        /* Sections */
        .section {
          padding: 100px 32px;
        }
        .section-inner {
          max-width: 1200px;
          margin: 0 auto;
        }
        .section-header {
          text-align: center;
          margin-bottom: 60px;
        }
        .section-tag {
          display: inline-block;
          padding: 4px 12px;
          background: rgba(255,90,54,0.1);
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          color: #FF7B5C;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 16px;
        }
        .section-header h2 {
          font-size: 36px;
          font-weight: 800;
          margin: 0 0 12px;
          color: #FFFFFF;
        }
        .section-header p {
          font-size: 16px;
          color: rgba(255,255,255,0.5);
          max-width: 500px;
          margin: 0 auto;
        }

        /* Features */
        .features-section {
          background: #0F0F13;
        }
        .features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        .feature-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: 32px;
          transition: all 0.3s;
        }
        .feature-card:hover {
          transform: translateY(-4px);
          border-color: rgba(255,90,54,0.2);
          background: rgba(255,255,255,0.05);
        }
        .feature-icon {
          width: 52px;
          height: 52px;
          background: linear-gradient(135deg, rgba(255,90,54,0.15), rgba(0,168,107,0.1));
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #FF7B5C;
          margin-bottom: 20px;
        }
        .feature-card h3 {
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 8px;
          color: #FFFFFF;
        }
        .feature-card p {
          font-size: 14px;
          line-height: 1.6;
          color: rgba(255,255,255,0.5);
          margin: 0;
        }

        /* Courses */
        .courses-section {
          background: #13131A;
        }
        .courses-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
        }
        .course-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 16px;
          padding: 36px;
          transition: all 0.3s;
        }
        .course-card:hover {
          border-color: rgba(0,168,107,0.3);
          background: rgba(255,255,255,0.05);
        }
        .course-code {
          display: inline-block;
          padding: 4px 10px;
          background: rgba(0,168,107,0.12);
          border-radius: 8px;
          font-size: 13px;
          font-weight: 700;
          color: #34D399;
          margin-bottom: 14px;
        }
        .course-card h3 {
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 10px;
          color: #FFFFFF;
        }
        .course-card p {
          font-size: 14px;
          line-height: 1.6;
          color: rgba(255,255,255,0.5);
          margin: 0 0 20px;
        }
        .course-meta {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .course-meta span {
          font-size: 13px;
          color: rgba(255,255,255,0.4);
          font-weight: 500;
        }
        .course-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          background: rgba(0,168,107,0.15);
          border: 1px solid rgba(0,168,107,0.3);
          border-radius: 8px;
          color: #34D399;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .course-btn:hover {
          background: rgba(0,168,107,0.25);
        }

        /* How It Works */
        .how-section {
          background: #0F0F13;
        }
        .steps-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        .step-card {
          text-align: center;
          padding: 40px 32px;
        }
        .step-num {
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, #FF5A36, #E04420);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: 800;
          color: white;
          margin: 0 auto 20px;
        }
        .step-card h3 {
          font-size: 20px;
          font-weight: 700;
          margin: 0 0 8px;
          color: #FFFFFF;
        }
        .step-card p {
          font-size: 14px;
          line-height: 1.6;
          color: rgba(255,255,255,0.5);
          margin: 0;
        }

        /* CTA */
        .cta-section {
          padding: 100px 32px;
          background: linear-gradient(135deg, rgba(255,90,54,0.08) 0%, rgba(0,168,107,0.06) 100%), #13131A;
        }
        .cta-inner {
          max-width: 700px;
          margin: 0 auto;
          text-align: center;
        }
        .cta-inner h2 {
          font-size: 36px;
          font-weight: 800;
          margin: 0 0 16px;
          color: #FFFFFF;
        }
        .cta-inner p {
          font-size: 16px;
          color: rgba(255,255,255,0.5);
          margin: 0 0 36px;
          line-height: 1.7;
        }
        .cta-actions {
          display: flex;
          gap: 14px;
          justify-content: center;
        }
        .cta-btn {
          padding: 14px 32px;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }
        .cta-btn.primary {
          background: linear-gradient(135deg, #FF5A36, #E04420);
          color: white;
        }
        .cta-btn.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(255,90,54,0.3);
        }
        .cta-btn.outline {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.2);
          color: #FFFFFF;
        }
        .cta-btn.outline:hover {
          border-color: rgba(255,255,255,0.4);
          background: rgba(255,255,255,0.06);
        }

        /* Footer */
        .landing-footer {
          padding: 40px 32px;
          background: #0A0A0E;
          border-top: 1px solid rgba(255,255,255,0.06);
        }
        .footer-inner {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .footer-brand {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          font-size: 15px;
          color: rgba(255,255,255,0.6);
        }
        .footer-links {
          display: flex;
          gap: 24px;
        }
        .footer-links a {
          color: rgba(255,255,255,0.4);
          text-decoration: none;
          font-size: 13px;
          transition: color 0.2s;
        }
        .footer-links a:hover {
          color: rgba(255,255,255,0.7);
        }
        .footer-copy {
          font-size: 12px;
          color: rgba(255,255,255,0.3);
        }

        /* Responsive */
        @media (max-width: 900px) {
          .hero-inner {
            flex-direction: column;
            gap: 40px;
          }
          .hero h1 {
            font-size: 32px;
          }
          .features-grid,
          .steps-grid {
            grid-template-columns: 1fr;
          }
          .courses-grid {
            grid-template-columns: 1fr;
          }
          .nav-links {
            display: none;
          }
          .footer-inner {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }
          .hero-visual {
            display: none;
          }
        }
      `}</style>
      <Watermark />
    </div>
  );
}
